import { notificationsService } from '@datacloud-incubation/frontend-notifications-module';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { catchError, EMPTY, from, Observable, switchMap, throwError } from 'rxjs';
import {
  CONTENT_TYPE,
  HTTP_HEADERS,
  HTTP_RESPONSE_STATUS,
  HTTP_METHODS
} from '@core/constants/apiService.constant';
import { Singleton } from '@core/decorators/Singleton.decorator';
import { refreshTokenMutation } from '@core/mutations/refreshToken.mutation';
import { errorResponseCreator } from '@core/utils/apiStreamService.util';
import { tokenService } from './Token.service';
import type { TStream, TGetStream, TPostStream } from '@core/models/apiStreamService.model';

@Singleton
class ApiStreamService {
  private stream$<Entity>({
    url,
    handlers,
    options = {},
    suppressError = false
  }: TStream): Observable<Entity> {
    return new Observable<Entity>((subscriber) => {
      void fetchEventSource(url, {
        headers: {
          [HTTP_HEADERS.Accept]: CONTENT_TYPE.Stream,
          [HTTP_HEADERS.ContentType]: CONTENT_TYPE.JSON,
          ...(tokenService.getAccessToken && {
            [HTTP_HEADERS.Authorization]: `Bearer ${tokenService.getAccessToken}`
          })
        },

        onopen: async (response) => {
          if (response.ok) {
            if (handlers?.onOpen) handlers.onOpen();
          } else if (
            response.status >= HTTP_RESPONSE_STATUS.badRequest &&
            response.status < HTTP_RESPONSE_STATUS.internalServerError &&
            response.status !== HTTP_RESPONSE_STATUS.tooManyRequests
          ) {
            subscriber.error(response);
          } else {
            subscriber.error(errorResponseCreator());
          }
        },
        onmessage: (message) => {
          try {
            const event = message.event;
            const data = JSON.parse(message.data) as unknown;
            const entity = { event, data } as Entity;

            subscriber.next(entity);
          } catch (error) {
            if (error instanceof Error) {
              subscriber.error(
                errorResponseCreator({
                  error: error.name,
                  message: error.message,
                  statusCode: HTTP_RESPONSE_STATUS.expectationFailed
                })
              );
            } else {
              subscriber.error(errorResponseCreator());
            }
          }
        },
        onclose: () => {
          if (handlers?.onClose) handlers.onClose();
          subscriber.complete();
        },
        onerror: (error: Error) => {
          subscriber.error(
            errorResponseCreator({
              error: error.name,
              message: error.message,
              statusCode: HTTP_RESPONSE_STATUS.notFound
            })
          );
        },
        openWhenHidden: true,
        ...options
      });
    }).pipe(
      catchError((errorResponse: Response) => {
        if (errorResponse.status === HTTP_RESPONSE_STATUS.unauthorized) {
          return refreshTokenMutation.refreshToken$().observer$.pipe(
            switchMap(() => {
              return this.stream$<Entity>({ url, options, suppressError });
            }),
            catchError(() => EMPTY)
          );
        }
        return from(errorResponse.json()).pipe(
          switchMap((parsedError: Error) => {
            notificationsService.errorNotification(parsedError.message);

            if (suppressError) {
              return EMPTY;
            }

            return throwError(() => parsedError);
          })
        );
      })
    );
  }

  public get$ = <Entity>({ url, options, handlers, suppressError }: TGetStream) =>
    this.stream$<Entity>({
      url,
      handlers,
      suppressError,
      options: {
        ...options,
        method: HTTP_METHODS.GET
      }
    });

  public post$ = <Entity>({ url, options, handlers, suppressError }: TPostStream) =>
    this.stream$<Entity>({
      url,
      handlers,
      suppressError,
      options: { ...options, method: HTTP_METHODS.POST }
    });
}

const apiStreamService = new ApiStreamService();

export { apiStreamService };
